'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';

// --- Types ---
interface ToolTab {
    id: string;
    name: string;
    desc: string;
    icon: string;
    color: string;
}

interface Issue {
    severity: 'Critical' | 'Warning' | 'Info';
    message: string;
    impact: string;
}

interface AuditResult {
    score: number;
    issues: Issue[];
    recommendations: string[];
    meta?: any;
}

// --- Sub-Components ---

const ScannerInterface = ({ isAnalyzing }: { isAnalyzing: boolean }) => (
    <div className="relative w-full aspect-square max-w-[280px] mx-auto bg-slate-950 rounded-full border border-slate-800 overflow-hidden flex flex-col items-center justify-center shadow-2xl shadow-brand-primary/20">
        {/* Grid Background */}
        <div className="absolute inset-0 opacity-20"
            style={{
                backgroundImage: 'linear-gradient(#334155 1px, transparent 1px), linear-gradient(90deg, #334155 1px, transparent 1px)',
                backgroundSize: '20px 20px'
            }}
        />

        {/* Radar Scan Effect */}
        <motion.div
            className="absolute inset-0 bg-gradient-to-b from-brand-primary/0 via-brand-primary/20 to-brand-primary/0"
            animate={{ top: ['-100%', '100%'] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        />

        <div className="z-10 text-center space-y-2">
            <div className="relative w-16 h-16 mx-auto flex items-center justify-center">
                <div className="absolute inset-0 border-2 border-brand-primary/30 rounded-full animate-ping-slow" />
                <div className="absolute inset-1 border-2 border-brand-primary/60 rounded-full animate-spin-slow border-t-transparent" />
                {/* Branded Center */}
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90 rounded-full backdrop-blur-sm border border-slate-700">
                    <Icon name="CpuChipIcon" size={24} className="text-brand-primary" />
                </div>
            </div>

            <div>
                <h4 className="text-white font-bold text-sm tracking-wide">Scanning...</h4>
                <div className="h-1 w-24 bg-slate-800 rounded-full mt-2 mx-auto overflow-hidden">
                    <motion.div
                        className="h-full bg-brand-primary"
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 2 }}
                    />
                </div>
            </div>
        </div>
    </div>
);

const ResultsCard = ({ result, toolName, onReset }: { result: AuditResult; toolName: string; onReset: () => void }) => {
    // Helper to get color/icon based on severity
    const getSeverityStyles = (severity: string) => {
        switch (severity) {
            case 'Critical': return { bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-700', icon: 'XCircleIcon', dot: 'bg-red-500' };
            case 'Warning': return { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700', icon: 'ExclamationTriangleIcon', dot: 'bg-amber-500' };
            case 'Info': return { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-700', icon: 'InformationCircleIcon', dot: 'bg-blue-500' };
            default: return { bg: 'bg-slate-50', border: 'border-slate-100', text: 'text-slate-700', icon: 'ExclamationCircleIcon', dot: 'bg-slate-500' };
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden mt-4">
            {/* Score Header */}
            <div className="p-6 text-center border-b border-slate-100 bg-slate-50/50">
                <div className="relative w-32 h-32 mx-auto mb-4">
                    <svg className="w-full h-full transform -rotate-90">
                        <circle cx="64" cy="64" r="56" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                        <motion.circle
                            cx="64" cy="64" r="56"
                            fill="none"
                            stroke={result.score > 80 ? "#10b981" : result.score > 50 ? "#f59e0b" : "#ef4444"}
                            strokeWidth="8"
                            strokeDasharray="351"
                            strokeDashoffset={351 - (351 * result.score) / 100}
                            initial={{ strokeDashoffset: 351 }}
                            animate={{ strokeDashoffset: 351 - (351 * result.score) / 100 }}
                            transition={{ duration: 1.5, ease: "easeOut" }}
                            strokeLinecap="round"
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-3xl font-bold text-slate-900">{result.score}</span>
                        <span className="text-[10px] text-slate-500 uppercase font-bold">Health</span>
                    </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900">{toolName} Complete</h3>
                <p className="text-sm text-slate-500 mt-1">
                    Status: <span className={result.score > 80 ? "text-green-600 font-bold" : "text-amber-600 font-bold"}>
                        {result.score > 80 ? "Healthy" : "Needs Attention"}
                    </span>
                </p>
            </div>

            {/* Issues List */}
            <div className="p-4 space-y-3 bg-white">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Key Findings</h4>
                {result.issues.length === 0 ? (
                    <div className="p-4 bg-green-50 rounded-xl border border-green-100 text-center">
                        <Icon name="CheckCircleIcon" size={24} className="text-green-500 mx-auto mb-2" />
                        <p className="text-sm font-medium text-green-700">No critical issues found!</p>
                    </div>
                ) : (
                    result.issues.slice(0, 3).map((issue, i) => {
                        const styles = getSeverityStyles(issue.severity);
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className={`p-3 rounded-xl border ${styles.bg} ${styles.border} flex items-start space-x-3`}
                            >
                                <Icon name={styles.icon as any} size={18} className={`${styles.text} mt-0.5 flex-shrink-0`} />
                                <div>
                                    <p className={`text-sm font-semibold ${styles.text}`}>{issue.message}</p>
                                    <p className="text-xs text-slate-500 mt-1 opacity-80">{issue.impact}</p>
                                </div>
                            </motion.div>
                        );
                    })
                )}
                {result.issues.length > 3 && (
                    <p className="text-xs text-center text-slate-400 italic">
                        + {result.issues.length - 3} more issues found
                    </p>
                )}
            </div>

            {/* Actions */}
            <div className="p-4 pt-0">
                <button className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold text-sm shadow-lg shadow-slate-900/10 mb-3">
                    Get Full Report
                </button>
                <button
                    onClick={onReset}
                    className="w-full py-3 text-slate-500 font-medium text-sm hover:bg-slate-50 rounded-xl transition-colors"
                >
                    Run Another Scan
                </button>
            </div>
        </div>
    );
}


const MobileInteractiveTools = () => {
    const [activeTab, setActiveTab] = useState<string>('seo-audit');
    const [websiteUrl, setWebsiteUrl] = useState<string>('');
    const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
    const [auditResult, setAuditResult] = useState<AuditResult | null>(null);

    const tools: ToolTab[] = [
        {
            id: 'seo-audit',
            name: 'SEO Scan',
            desc: 'Tech & Meta',
            icon: 'MagnifyingGlassIcon',
            color: 'bg-brand-primary'
        },
        {
            id: 'gbp-checker',
            name: 'Local Vis.',
            desc: 'Google Maps',
            icon: 'MapPinIcon',
            color: 'bg-green-500'
        },
        {
            id: 'competitor',
            name: 'Comp. Spy',
            desc: 'Rankings',
            icon: 'ChartBarIcon',
            color: 'bg-purple-500'
        }
    ];

    const handleAudit = async () => {
        if (!websiteUrl) return;
        setIsAnalyzing(true);
        setAuditResult(null);

        // Mimic API delay and response for frontend demo
        // In real impl, fetch from API
        setTimeout(() => {
            // Mock Data
            let mockScore = Math.floor(Math.random() * 40) + 50;
            const mockIssues: Issue[] = [
                { severity: 'Critical', message: 'Missing Meta Description', impact: 'Lowers CTR on search results.' },
                { severity: 'Warning', message: 'Slow Page Load (2.4s)', impact: 'May affect bounce rates.' },
                { severity: 'Info', message: 'H1 Tag Optimized', impact: 'Good for keyword relevance.' }
            ];

            setAuditResult({
                score: mockScore,
                issues: mockIssues,
                recommendations: ['Add meta descriptions', 'Optimize images', 'Check server response time'],
                meta: { load_time: '2.4s', page_size: '1.2MB' }
            });
            setIsAnalyzing(false);
        }, 3000);
    };

    return (
        <section className="py-16 bg-slate-50 relative overflow-hidden">

            <div className="container mx-auto px-4 relative z-10">
                {/* Header */}
                <div className="text-center mb-8">
                    <span className="inline-block py-1 px-3 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider mb-3">
                        Free Tools
                    </span>
                    <h2 className="text-3xl font-bold text-slate-900 leading-tight">
                        Visibility <span className="text-brand-primary">Lab</span>
                    </h2>
                    <p className="text-slate-600 mt-2 text-sm max-w-xs mx-auto">
                        Check your digital health instantly. Select a tool:
                    </p>
                </div>

                {/* Mobile Pill Navigation */}
                <div className="flex space-x-3 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide snap-x">
                    {tools.map((tool) => (
                        <button
                            key={tool.id}
                            onClick={() => {
                                setActiveTab(tool.id);
                                setAuditResult(null);
                                setWebsiteUrl('');
                            }}
                            className={`flex items-center space-x-2 px-4 py-3 rounded-xl border whitespace-nowrap transition-all snap-start ${activeTab === tool.id
                                    ? 'bg-slate-900 border-slate-900 text-white shadow-lg shadow-slate-900/20'
                                    : 'bg-white border-slate-200 text-slate-600 shadow-sm'
                                }`}
                        >
                            <Icon name={tool.icon as any} size={18} className={activeTab === tool.id ? 'text-brand-primary' : 'text-slate-400'} />
                            <span className="font-semibold text-sm">{tool.name}</span>
                        </button>
                    ))}
                </div>

                {/* Main Action Area */}
                <AnimatePresence mode="wait">
                    {isAnalyzing ? (
                        <motion.div
                            key="scanning"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="py-12"
                        >
                            <ScannerInterface isAnalyzing={isAnalyzing} />
                            <p className="text-center text-slate-500 text-sm mt-6 animate-pulse">
                                Analyzing {websiteUrl.replace(/^https?:\/\//, '')}...
                            </p>
                        </motion.div>
                    ) : auditResult ? (
                        <motion.div
                            key="results"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <ResultsCard
                                result={auditResult}
                                toolName={tools.find(t => t.id === activeTab)?.name || 'Audit'}
                                onReset={() => setAuditResult(null)}
                            />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="input"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="mt-4 bg-white rounded-2xl p-6 border border-slate-200 shadow-xl shadow-slate-200/50"
                        >
                            <div className="flex items-center space-x-3 mb-6">
                                <div className={`p-3 rounded-xl bg-slate-50 border border-slate-100`}>
                                    <Icon name={tools.find(t => t.id === activeTab)?.icon as any} size={24} className="text-brand-primary" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900 text-lg">
                                        {tools.find(t => t.id === activeTab)?.name}
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        {tools.find(t => t.id === activeTab)?.desc}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Icon name="GlobeAltIcon" size={20} className="text-slate-400" />
                                    </div>
                                    <input
                                        type="url"
                                        value={websiteUrl}
                                        onChange={(e) => setWebsiteUrl(e.target.value)}
                                        placeholder="yourwebsite.com"
                                        className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-all placeholder:text-slate-400 font-medium text-base h-12"
                                    />
                                </div>
                                <button
                                    onClick={handleAudit}
                                    disabled={!websiteUrl}
                                    className="w-full py-4 bg-gradient-to-r from-brand-primary to-brand-secondary text-white rounded-xl font-bold text-lg shadow-lg shadow-brand-primary/25 hover:shadow-brand-primary/40 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Start Analysis
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

            </div>
        </section>
    );
};

export default MobileInteractiveTools;
